import { useConfigStore } from '@xantory/store';

/** What a SITE-SCOPED query is bound to, and whether it may run yet.
 *
 * ⚠ WHY THIS EXISTS. In cloud mode `resolvePath` rewrites `/v1/*` to
 * `/sites/{siteId}/v1/*` and THROWS `NO_SITE_SELECTED` when no site is picked
 * yet. A query that fires during that window caches the error — and if its key
 * does not mention the site, the key never changes when the site arrives, so it
 * never refetches. The screen then sits on a failed query forever.
 *
 * That is not theoretical: it is why `+ New request` was missing on the cloud
 * console while the same account, the same permissions and the same readiness
 * endpoint all worked perfectly at the site. `useProcurementReadiness` returned
 * `undefined`, and `canRaise && readiness?.ready` quietly hid the button while
 * the list rendered normally beside it.
 *
 * `scope` goes in the query key so switching sites refetches instead of serving
 * the previous farm's answer. `ready` gates the fetch so it never runs during
 * the window that throws in the first place.
 */
export function useSiteScope(): { scope: string; ready: boolean } {
  const mode = useConfigStore((s) => s.mode);
  const siteId = useConfigStore((s) => s.siteId);
  // Local mode is implicitly one site and needs no picker.
  if (mode !== 'cloud') return { scope: 'local', ready: true };
  return { scope: siteId ?? 'no-site', ready: Boolean(siteId) };
}
